import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardHeader from '../components/DashboardHeader';
import Toast from '../components/Toast';
import './CartPage.css';
import '../styles/pageAnimation.css';

function CartPage() {
  const [cartItems, setCartItems] = useState([]);
  const role = localStorage.getItem('role');
  // Promo code handling moved to checkout; keep cart focused on items only
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showGreenBar, setShowGreenBar] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Delivery fee should not be shown/applied in cart page

  const navigate = useNavigate();

  // Handle responsive breakpoint
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hide green bar on scroll (no animation, instant show/hide)
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY === 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchCart = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/cart`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!res.ok) throw new Error('Failed to fetch cart');
      const data = await res.json();
      setCartItems(data.items || []);
    } catch (err) {
      console.error('Error fetching cart:', err);
      setToastMessage('Could not load cart items.');
      setShowToast(true);
    }
  };

  useEffect(() => {
    // Optimistic load: show cart immediately, no loading spinner on initial load
    fetchCart();
  }, []);

  const updateQuantity = async (productId, quantity, maxStock) => {
    if (quantity < 1) return;
    if (quantity > maxStock) {
      setToastMessage(`Only ${maxStock} in stock.`);
      setShowToast(true);
      return;
    }

    try {
      // Update locally first for instant feedback
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.product._id === productId ? { ...item, quantity } : item
        )
      );

      await fetch(`${process.env.REACT_APP_API}/api/cart`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ productId, quantity }),
      });
    } catch (err) {
      console.error('Error updating cart item:', err);
      // Revert on error
      fetchCart();
    }
  };

  const removeFromCart = async (productId, productName) => {
    try {
      // Optimistically remove from UI first
      setCartItems(prevItems => prevItems.filter(item => item.product._id !== productId));
      
      await fetch(`${process.env.REACT_APP_API}/api/cart/${productId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      setToastMessage(`${productName} removed from cart!`);
      setShowToast(true);
      // Dispatch cart update event
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (err) {
      console.error('Error removing cart item:', err);
      setToastMessage('Failed to remove item.');
      setShowToast(true);
      // Revert on error
      fetchCart();
    }
  };

  // Use discounted price for subtotal calculation
  const subtotal = cartItems.reduce((acc, item) => {
    // Skip if product no longer exists
    if (!item.product) return acc;
    
    const hasDiscount = item.product.discount && Number(item.product.discount) > 0;
    const price = hasDiscount
      ? (Number(item.product.price) - (Number(item.product.price) * (Number(item.product.discount) / 100)))
      : Number(item.product.price);
    return acc + price * item.quantity;
  }, 0);
  // Promo code logic removed from cart; promo codes are applied during checkout
  const total = subtotal;

  const handleCheckout = () => {
    navigate('/checkout', { state: { cart: cartItems } });
  };

  const emptyCartVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.4 }
    }
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) - only visible at top */}
      {showGreenBar && (
        <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
      )}
      {/* Navbar always sticky at top */}
      <div style={{ 
        width: '100%', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
        position: 'sticky',
        top: 0, 
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>
      <div style={{ width: '100%', flex: 1 }} className="page-content-fade-in">

      {/* Main Content */}
      <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '20px 16px' : '40px 24px' }}>
        
        {role === 'admin' ? (
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={emptyCartVariants}
            style={{ 
              padding: '3rem 1.5rem', 
              textAlign: 'center', 
              background: '#fff',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              maxWidth: '600px',
              margin: '0 auto'
            }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🛡️</div>
            <h2 style={{ fontSize: isMobile ? '1.5rem' : '1.75rem', color: '#1e293b', marginBottom: '0.75rem', fontWeight: 700 }}>
              Admin Access Only
            </h2>
            <p style={{ fontSize: isMobile ? '0.95rem' : '1.05rem', color: '#64748b', lineHeight: 1.6 }}>
              Admins cannot use the shopping cart. Please use the admin dashboard to manage products and orders.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/admin/dashboard')}
              style={{
                marginTop: '1.5rem',
                padding: '12px 32px',
                background: '#15803d',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                minHeight: '48px'
              }}
            >
              Go to Dashboard
            </motion.button>
          </motion.div>
        ) : (
          <div className="cart-container">
            {/* Cart Items Section */}
            <div className="cart-items-section">
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: isMobile ? '20px' : '28px'
              }}>
                <h1 style={{ 
                  fontSize: isMobile ? '1.75rem' : '2rem', 
                  fontWeight: 700,
                  color: '#1e293b',
                  margin: 0
                }}>
                  Shopping Cart
                </h1>
                {cartItems.length > 0 && (
                  <span style={{
                    background: '#15803d',
                    color: '#fff',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}>
                    {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                  </span>
                )}
              </div>

              {cartItems.length === 0 ? (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={emptyCartVariants}
                  style={{
                    padding: isMobile ? '3rem 1.5rem' : '4rem 2rem',
                    textAlign: 'center',
                    background: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
                  }}
                >
                  <div style={{ fontSize: '5rem', marginBottom: '1.5rem' }}>🛒</div>
                  <h2 style={{ 
                    fontSize: isMobile ? '1.5rem' : '1.75rem', 
                    color: '#1e293b', 
                    marginBottom: '0.75rem',
                    fontWeight: 700
                  }}>
                    Your cart is empty
                  </h2>
                  <p style={{ 
                    fontSize: isMobile ? '0.95rem' : '1.05rem', 
                    color: '#64748b', 
                    marginBottom: '2rem',
                    lineHeight: 1.6
                  }}>
                    Add some beautiful plants to your cart and watch your garden grow!
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/catalog')}
                    style={{
                      padding: '14px 40px',
                      background: '#15803d',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      minHeight: '48px'
                    }}
                  >
                    Browse Catalog
                  </motion.button>
                </motion.div>
              ) : (
              <div className="cart-items-list">
                {cartItems
                  .filter(item => item.product) // Filter out items with null products
                  .map(({ product, quantity }) => (
                    <motion.div 
                      key={product._id} 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      whileHover={{ y: -2 }}
                      className="cart-item-card"
                    >
                      <div 
                        className="cart-item-image-wrapper"
                        onClick={() => navigate(`/product/${product._id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="cart-item-image"
                        />
                        {product.discount && Number(product.discount) > 0 ? (
                          <div className="discount-badge">
                            -{product.discount}%
                          </div>
                        ) : null}
                      </div>

                      <div className="cart-item-details">
                        <h3 className="cart-item-name">{product.name}</h3>
                        
                        <div className="cart-item-price-wrapper">
                          {product.discount && Number(product.discount) > 0 ? (
                            <>
                              <span className="cart-item-price-original">
                                ₱{Number(product.price).toFixed(2)}
                              </span>
                              <span className="cart-item-price-discounted">
                                ₱{(Number(product.price) - (Number(product.price) * (Number(product.discount) / 100))).toFixed(2)}
                              </span>
                            </>
                          ) : (
                            <span className="cart-item-price-current">
                              ₱{Number(product.price).toFixed(2)}
                            </span>
                          )}
                        </div>

                        <div className="cart-item-stock-info">
                          <span style={{ 
                            color: product.stock < 10 ? '#dc2626' : '#64748b',
                            fontSize: '0.85rem',
                            fontWeight: 500
                          }}>
                            {product.stock < 10 ? `Only ${product.stock} left!` : `${product.stock} in stock`}
                          </span>
                        </div>

                        <div className="cart-item-actions">
                          <div className="quantity-controls">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => updateQuantity(product._id, quantity - 1, product.stock)}
                              disabled={quantity <= 1}
                              className="quantity-btn"
                              aria-label="Decrease quantity"
                            >
                              −
                            </motion.button>
                            <input
                              type="number"
                              value={quantity}
                              min="1"
                              max={product.stock}
                              disabled={false}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                if (val >= 1 && val <= product.stock) {
                                  updateQuantity(product._id, val, product.stock);
                                }
                              }}
                              className="quantity-input"
                              aria-label="Quantity"
                            />
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => updateQuantity(product._id, quantity + 1, product.stock)}
                              disabled={quantity >= product.stock}
                              className="quantity-btn"
                              aria-label="Increase quantity"
                            >
                              +
                            </motion.button>
                          </div>

                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => removeFromCart(product._id, product.name)}
                            className="remove-btn"
                            aria-label="Remove from cart"
                          >
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24" style={{ marginRight: '6px' }}>
                              <path d="M3 6h18v2H3V6zm2 3h14v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm3 2v9h2v-9H8zm4 0v9h2v-9h-2z"/>
                            </svg>
                            Remove
                          </motion.button>
                        </div>

                        <div className="cart-item-subtotal">
                          <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Subtotal:</span>
                          <span style={{ color: '#15803d', fontWeight: 700, fontSize: '1.1rem' }}>
                            ₱{(
                              (product.discount && Number(product.discount) > 0
                                ? Number(product.price) - (Number(product.price) * (Number(product.discount) / 100))
                                : Number(product.price)) * quantity
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Order Summary - Sticky on Desktop */}
            {cartItems.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="order-summary-card"
              >
                <h2 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '24px'
                }}>
                  Order Summary
                </h2>

                {/* Product List */}
                <div style={{ marginBottom: '20px' }}>
                  {cartItems.map(({ product, quantity }) => (
                    <div 
                      key={product._id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        marginBottom: '12px',
                        paddingBottom: '12px',
                        borderBottom: '1px solid #f1f5f9'
                      }}
                    >
                      <div style={{ flex: 1, marginRight: '12px' }}>
                        <div style={{ 
                          fontSize: '0.9rem', 
                          color: '#1e293b',
                          fontWeight: 500,
                          marginBottom: '4px',
                          lineHeight: 1.3
                        }}>
                          {product.name}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                          Qty: {quantity}
                        </div>
                      </div>
                      <div style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: 600,
                        color: '#15803d',
                        whiteSpace: 'nowrap'
                      }}>
                        ₱{(
                          (product.discount && Number(product.discount) > 0
                            ? Number(product.price) - (Number(product.price) * (Number(product.discount) / 100))
                            : Number(product.price)) * quantity
                        ).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="summary-line">
                  <span>Subtotal</span>
                  <strong>₱{subtotal.toFixed(2)}</strong>
                </div>

                {/* Promo discounts are applied at checkout; no discount shown here */}

                <hr style={{ 
                  margin: '20px 0', 
                  border: 'none', 
                  borderTop: '2px solid #e2e8f0' 
                }} />

                <div className="summary-total">
                  <span>Total</span>
                  <strong>₱{total.toFixed(2)}</strong>
                </div>

                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="checkout-btn"
                  onClick={handleCheckout}
                >
                  <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                    Proceed to Checkout
                  </span>
                  <span style={{ fontSize: '1.2rem' }}>→</span>
                </motion.button>

                <p style={{ 
                  fontSize: '0.85rem', 
                  color: '#94a3b8', 
                  textAlign: 'center',
                  marginTop: '16px',
                  lineHeight: 1.5
                }}>
                  Delivery fee will be calculated at checkout
                </p>
              </motion.div>
            )}
          </div>
        )}
      </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {showToast && <Toast message={toastMessage} onClose={() => setShowToast(false)} />}
    </div>
  );
}

export default CartPage;
